import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import TrackMapImageField from "./TrackMapImageField";
import { emptyStage } from "../utils/route.utils";

const PointFields = ({
  label,
  point,
  errors = {},
  disabled,
  onChange,
}) => {
  return (
    <div className="mb-3">
      <Form.Label className="fw-semibold">{label}</Form.Label>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Title</Form.Label>
            <Form.Control
              type="text"
              value={point?.title || ""}
              onChange={(e) => onChange({ ...point, title: e.target.value })}
              placeholder="Derawar Fort"
              isInvalid={!!errors.title}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {errors.title}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Latitude</Form.Label>
            <Form.Control
              type="number"
              step="any"
              value={point?.lat ?? ""}
              onChange={(e) => onChange({ ...point, lat: e.target.value })}
              placeholder="28.7667"
              isInvalid={!!errors.lat}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lat}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Longitude</Form.Label>
            <Form.Control
              type="number"
              step="any"
              value={point?.lng ?? ""}
              onChange={(e) => onChange({ ...point, lng: e.target.value })}
              placeholder="71.3333"
              isInvalid={!!errors.lng}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lng}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

const RouteForm = ({
  form,
  validationErrors,
  submitting,
  submitLabel = "Save Route",
  submittingLabel = "Saving...",
  isEditMode = false,
  readOnly = false,
  onSubmit,
  onFieldChange,
  onBack,
}) => {
  const disabled = submitting || readOnly;
  const stages = form.stages || [];

  const updateStage = (index, patch) => {
    const next = stages.map((stage, i) =>
      i === index ? { ...stage, ...patch } : stage
    );
    onFieldChange("stages", next);
  };

  const addStage = () => {
    onFieldChange("stages", [...stages, emptyStage()]);
  };

  const removeStage = (index) => {
    const next = stages.filter((_, i) => i !== index);
    onFieldChange("stages", next);
  };

  return (
    <Form onSubmit={onSubmit}>
      <h5 className="mb-3 mt-2">Basic Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.title}
              onChange={(e) => onFieldChange("title", e.target.value)}
              placeholder="Main Desert Route"
              isInvalid={!!validationErrors.title}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.title}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Total Distance (km) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.1"
              value={form.total_distance_km}
              onChange={(e) =>
                onFieldChange("total_distance_km", e.target.value)
              }
              placeholder="350"
              isInvalid={!!validationErrors.total_distance_km}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.total_distance_km}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Estimated Time</Form.Label>
            <Form.Control
              type="text"
              value={form.estimated_time}
              onChange={(e) => onFieldChange("estimated_time", e.target.value)}
              placeholder="3-5 Hours"
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={form.status}
              onChange={(e) => onFieldChange("status", e.target.value)}
              isInvalid={!!validationErrors.status}
              disabled={disabled}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.status}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Main competitive route across the desert."
              disabled={disabled}
            />
            {isEditMode ? (
              <Form.Text className="text-muted">
                Leave empty to clear the description.
              </Form.Text>
            ) : null}
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Track Map</h5>
      <TrackMapImageField
        imagePath={form.track_map_image || ""}
        imageFile={form.trackMapFile}
        disabled={disabled}
        error={validationErrors.track_map_image}
        helpText={
          isEditMode
            ? "Optional. Replacing the image uploads via PATCH /routes/:id/track-map (field name image)."
            : "Optional. Sent as multipart field track_map_image when creating."
        }
        onFileChange={(file) => onFieldChange("trackMapFile", file)}
      />

      <h5 className="mb-2 mt-3">Stages</h5>
      <p className="text-muted small mb-3">
        Optional. Each stage needs a unique stage number. Starting and ending
        points are optional; if provided, title/lat/lng are all required.
        {isEditMode
          ? " Stages with an existing _id are updated; new stages are created. Omitted stages are not deleted."
          : ""}
      </p>

      {stages.length === 0 ? (
        <p className="text-muted mb-3">No stages added yet.</p>
      ) : (
        stages.map((stage, index) => {
          const stageError = validationErrors.stages?.[index] || {};

          return (
            <div
              key={stage._id || `stage-${index}`}
              className="border rounded p-3 mb-3"
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Stage {index + 1}</h6>
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  disabled={disabled}
                  onClick={() => removeStage(index)}
                >
                  Remove
                </Button>
              </div>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Stage Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      value={stage.stage_number}
                      onChange={(e) =>
                        updateStage(index, { stage_number: e.target.value })
                      }
                      placeholder="1"
                      isInvalid={!!stageError.stage_number}
                      disabled={disabled}
                    />
                    <Form.Control.Feedback type="invalid">
                      {stageError.stage_number}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Stage Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={stage.stage_name}
                      onChange={(e) =>
                        updateStage(index, { stage_name: e.target.value })
                      }
                      placeholder="Inaugural Stage"
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <PointFields
                    label="Starting Point"
                    point={stage.startingPoint}
                    errors={stageError.startingPoint || {}}
                    disabled={disabled}
                    onChange={(nextPoint) =>
                      updateStage(index, { startingPoint: nextPoint })
                    }
                  />
                </Col>

                <Col md={12}>
                  <PointFields
                    label="Ending Point"
                    point={stage.endingPoint}
                    errors={stageError.endingPoint || {}}
                    disabled={disabled}
                    onChange={(nextPoint) =>
                      updateStage(index, { endingPoint: nextPoint })
                    }
                  />
                </Col>

                <Col md={12}>
                  <Form.Group className="mb-0">
                    <Form.Label>Stage Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={stage.stage_description}
                      onChange={(e) =>
                        updateStage(index, {
                          stage_description: e.target.value,
                        })
                      }
                      placeholder="Opening desert leg"
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          );
        })
      )}

      <Button
        type="button"
        variant="outline-primary"
        className="mb-4"
        disabled={disabled}
        onClick={addStage}
      >
        + Add Stage
      </Button>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          type="button"
          variant="outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={disabled}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RouteForm;
